import rawSuite from "./draft-jurkovikj-json-three-way-merge-00-vectors.json";
import provenance from "./provenance.json";
import type {
  ExpectedMergeResult,
  MergeVector,
  ScenarioExplanation,
  VectorCategory,
} from "../merge/types";

interface RawVector {
  readonly id: string;
  readonly base: string;
  readonly source: string;
  readonly target: string;
  readonly expected: ExpectedMergeResult;
  readonly assertion?: string;
}

type RawSuite = Readonly<Record<VectorCategory, readonly RawVector[]>> & {
  readonly schema: string;
  readonly draft: string;
};

const suite = rawSuite as RawSuite;

export const VECTOR_PROVENANCE = Object.freeze({
  schema: provenance.schema,
  sourceDocument: provenance.source_document,
  sourceUrl: provenance.source_url,
  sourceBytes: provenance.source_bytes,
  sourceSha256: provenance.source_sha256,
  vectorCount: provenance.vector_count,
});

export const CATEGORY_LABELS: Readonly<Record<VectorCategory, string>> = {
  successful_merges: "Successful merges",
  conflicts: "Structured conflicts",
  unsupported_arrays: "Arrays fail closed",
  invalid_input_precedence: "Invalid input and precedence",
};

const DRAFT_URL =
  "https://datatracker.ietf.org/doc/html/draft-jurkovikj-json-three-way-merge-00";

export interface DraftSectionLink {
  readonly href: string;
  readonly label: string;
}

const SECTION_BY_CATEGORY: Readonly<Record<VectorCategory, DraftSectionLink>> = {
  successful_merges: {
    href: `${DRAFT_URL}#section-6`,
    label: "Read Section 6: deterministic merge",
  },
  conflicts: {
    href: `${DRAFT_URL}#section-7`,
    label: "Read Section 7: conflicts and ordering",
  },
  unsupported_arrays: {
    href: `${DRAFT_URL}#section-5.3`,
    label: "Read Section 5.3: unsupported arrays",
  },
  invalid_input_precedence: {
    href: `${DRAFT_URL}#section-5`,
    label: "Read Section 5: validation and precedence",
  },
};

const SECTION_BY_VECTOR: Readonly<Record<string, DraftSectionLink>> = {
  "one-sided-add-null": {
    href: `${DRAFT_URL}#section-6.4`,
    label: "Read Section 6.4: member presence",
  },
  "large-integer-normalization": {
    href: `${DRAFT_URL}#section-4.2`,
    label: "Read Section 4.2: binary64 numbers",
  },
  "negative-zero-normalization": {
    href: `${DRAFT_URL}#section-4.2`,
    label: "Read Section 4.2: binary64 numbers",
  },
  "decoded-token-sequence-order": {
    href: `${DRAFT_URL}#section-7.3`,
    label: "Read Section 7.3: path ordering",
  },
  "utf16-path-order": {
    href: `${DRAFT_URL}#section-7.3`,
    label: "Read Section 7.3: path ordering",
  },
  "decoded-duplicate": {
    href: `${DRAFT_URL}#section-4.1`,
    label: "Read Section 4.1: strict JSON input",
  },
  "leading-bom": {
    href: `${DRAFT_URL}#section-4.1`,
    label: "Read Section 4.1: strict JSON input",
  },
};

const CATEGORY_ORDER: readonly VectorCategory[] = [
  "successful_merges",
  "conflicts",
  "unsupported_arrays",
  "invalid_input_precedence",
];

export const MERGE_VECTORS: readonly MergeVector[] = Object.freeze(
  CATEGORY_ORDER.flatMap((category) =>
    suite[category].map((entry) => ({ ...entry, category })),
  ),
);

const EXPLANATIONS: Readonly<Record<string, ScenarioExplanation>> = {
  "independent-members": {
    title: "Independent edits compose",
    summary:
      "Source changes a while Target changes b. Neither edit competes at the same member path, so both survive.",
    rule: "Object members are merged recursively after the equality shortcuts.",
  },
  "one-sided-add-null": {
    title: "Null is present, not missing",
    summary:
      "Source adds x with the value null. Target leaves x absent. The null value is preserved as an intentional addition.",
    rule: "Missing membership and a present null value are different states.",
  },
  "large-integer-normalization": {
    title: "Numbers compare after binary64 normalization",
    summary:
      "The two large integer spellings normalize to the same finite binary64 value, activating the one-sided shortcut.",
    rule: "Textual number spelling is not the merge equality relation.",
  },
  "negative-zero-normalization": {
    title: "Negative zero normalizes to positive zero",
    summary:
      "All three inputs represent the same normalized zero value. A successful result never emits negative zero.",
    rule: "Finite binary64 normalization maps -0 to +0 for merge equality and output.",
  },
  "scalar-divergence": {
    title: "Divergent scalar edits conflict",
    summary:
      "Source and Target both changed the root scalar differently from Base. No application-specific winner is invented.",
    rule: "Divergent same-kind scalar changes produce concurrent_value_change.",
  },
  "decoded-token-sequence-order": {
    title: "Paths sort by decoded token sequence",
    summary:
      "The nested /a/b path sorts before the one-token /a!b path even though comparing rendered pointer strings would disagree.",
    rule: "Conflict ordering compares decoded RFC 6901 tokens, not rendered pointer bytes.",
  },
  "utf16-path-order": {
    title: "Pointer tokens use UTF-16 code-unit order",
    summary:
      "The supplementary grinning-face token sorts before the BMP private-use token because its leading UTF-16 surrogate is smaller.",
    rule: "Token ordering deliberately follows unsigned UTF-16 code units.",
  },
  "equal-arrays": {
    title: "Arrays fail closed before equality shortcuts",
    summary:
      "Even identical arrays are outside the base merge domain. Equality does not make unsupported input admissible.",
    rule: "All three inputs are admitted before any merge equality shortcut runs.",
  },
  "least-array-path": {
    title: "Array diagnostics choose the least path",
    summary:
      "The selected path is /a/items rather than the first array encountered in source text.",
    rule: "Array selection uses deterministic pointer order, not parser encounter order.",
  },
  "invalid-base-first": {
    title: "Input-side precedence is deterministic",
    summary:
      "The malformed Base document is reported before the unsupported array in Source is considered.",
    rule: "Input admission proceeds Base, then Source, then Target and stops at the first prohibited input.",
  },
  "decoded-duplicate": {
    title: "Duplicate decoded member names are rejected",
    summary:
      "The spellings a and \\u0061 decode to the same object member name. Last-wins parsing is not allowed.",
    rule: "Duplicate object names are detected after JSON string decoding.",
  },
  "leading-bom": {
    title: "The strict input profile rejects a BOM",
    summary:
      "A leading U+FEFF is not silently ignored by the merge input profile.",
    rule: "The submitted JSON text must satisfy the draft's strict UTF-8 input rules.",
  },
};

function humanize(identifier: string): string {
  return identifier
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function explainVector(vector: MergeVector): ScenarioExplanation {
  const specific = EXPLANATIONS[vector.id];
  if (specific !== undefined) {
    return specific;
  }

  const fallbackByCategory: Readonly<Record<VectorCategory, string>> = {
    successful_merges:
      "The equality shortcuts and recursive object rules produce one complete merged value.",
    conflicts:
      "Competing edits are preserved as ordered, typed conflicts rather than silently resolved.",
    unsupported_arrays:
      "An array appears outside an array-aware profile, so the operation fails closed.",
    invalid_input_precedence:
      "Strict input processing reports one deterministic admission failure.",
  };

  return {
    title: humanize(vector.id),
    summary: fallbackByCategory[vector.category],
    rule: `Published Appendix B vector: ${vector.id}.`,
  };
}

export function draftSectionForVector(
  vector: MergeVector,
): DraftSectionLink {
  return SECTION_BY_VECTOR[vector.id] ?? SECTION_BY_CATEGORY[vector.category];
}

export function findVector(identifier: string): MergeVector | undefined {
  return MERGE_VECTORS.find((vector) => vector.id === identifier);
}
